import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAssetsStore } from '../../hooks/useAssetsStore';
import { AssetService, AssetUtils } from '../../services/asset/asset-api-service';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '../../apis/api-config';
import axios from 'axios';
import { getStorage } from '../../hooks/useAuth';
import { AssetFormMapper, FormValidator } from '../../services/asset/asset-models';
import { getCurrentFormConfigs } from '../../services/languageService';
import { getFormCategoryFromSystemType } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { updateAssetInList } from '../../store/assets/assetsSlice'; // adjust path as needed
// import updateAsset from "../../store/assets/assetsSlice";
import { AxiosError } from 'axios' 
import type { RootState, AppDispatch } from '../../store';


import './Home.css';

const AssetDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const { t } = useTranslation();

    const [assetDetails, setAssetDetails] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Edit mode states - updated for universal inline editing
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingField, setEditingField] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [editFormData, setEditFormData] = useState<any>({});
    const [editFormType, setEditFormType] = useState<string>('');
    const [editLoading, setEditLoading] = useState(false);
    const [editingCircuits, setEditingCircuits] = useState<any[]>([]);
    const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: string } | null>(null);
    const { getAssetById } = useAssetsStore();
    const selectedAsset = id ? getAssetById(parseInt(id)) : null;
    const token = getStorage().getItem('access_token');
    const FORM_CONFIGS = getCurrentFormConfigs();

    const { currentUser } = useSelector((state: RootState) => state.currentUser);
    const userRole = currentUser?.organizationUserRole;


    useEffect(() => {
        const loadAssetDetails = async () => {
            if (!selectedAsset) return;

            try {
                setLoading(true);
                const fullDetails = await AssetService.getAsset(selectedAsset.id);
                setAssetDetails(fullDetails);
            } catch (error) {
                console.error('Error loading asset details:', error);
                setMessage({ type: 'error', text: 'Failed to load asset details' });
            } finally {
                setLoading(false);
            }
        };

        loadAssetDetails();
    }, [selectedAsset]);

    // Universal inline editing functions
    const startFieldEdit = (fieldKey: string, currentValue: any) => {
        setEditingField(fieldKey);
        setEditValue(String(currentValue || ''));
    };

    const cancelFieldEdit = () => {
        setEditingField(null);
        setEditValue('');
    };

    const saveFieldEdit = () => {
        if (editingField) {
            setEditFormData(prev => ({
                ...prev,
                [editingField]: editValue
            }));
            setEditingField(null);
            setEditValue('');
        }
    };

    const handleFieldKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            saveFieldEdit();
        } else if (e.key === 'Escape') {
            cancelFieldEdit();
        }
    };

    // Circuit inline editing functions
    const startCellEdit = (rowIndex: number, field: string, currentValue: string) => {
        setEditingCell({ rowIndex, field });
        setEditValue(currentValue);
    };

    const startDriverTypeEdit = (rowIndex: number, currentValue: number) => {
        setEditingCell({ rowIndex, field: 'driverTypes' });
        setEditValue(currentValue.toString());
    };

    const cancelCellEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const saveCellEdit = () => {
        if (editingCell) {
            const updatedCircuits = [...editingCircuits];
            const { rowIndex, field } = editingCell;

            if (field === 'maxCurrent' || field === 'continousCurrent') {
                updatedCircuits[rowIndex][field] = parseFloat(editValue) || 0;
            } else if (field === 'driverTypes') {
                updatedCircuits[rowIndex][field] = parseInt(editValue) || 1;
            } else {
                updatedCircuits[rowIndex][field] = editValue;
            }

            setEditingCircuits(updatedCircuits);
            setEditingCell(null);
            setEditValue('');
        }
    };

    const handleCellKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            saveCellEdit();
        } else if (e.key === 'Escape') {
            cancelCellEdit();
        }
    };

    // Edit functionality methods
    const handleEditAsset = async () => {
        if (!assetDetails) return;

        try {
            const assetSystemType = assetDetails.assetType?.assetSystemType || assetDetails.type?.assetSystemType;

            if (!assetSystemType) {
                setMessage({ type: 'error', text: 'Asset type information is missing' });
                return;
            }

            const formType = getFormCategoryFromSystemType(assetSystemType);

            if (formType === 'unknown') {
                setMessage({ type: 'error', text: 'This asset type cannot be edited' });
                return;
            }

            // Map API asset to form data
            const { form } = AssetFormMapper.mapFromApiAsset(assetDetails);

            const formWithAssetType = {
                ...form,
                assetTypeId: assetDetails.assetType?.id || assetDetails.type?.id
            };

            setEditFormData(formWithAssetType);
            setEditFormType(formType);

            // Handle circuits for distribution boards
            if (formType === 'distribution_board' && assetDetails.assetDistributionBoard?.assetDistributionBoardCircuits) {
                setEditingCircuits([...assetDetails.assetDistributionBoard.assetDistributionBoardCircuits]);
            } else {
                setEditingCircuits([]);
            }

            setIsEditMode(true);

            console.log('Edit form data:', formWithAssetType);
            console.log('Form type:', formType);
            console.log('Asset system type:', assetSystemType);
        } catch (error) {
            console.error('Error preparing asset for edit:', error);
            setMessage({ type: 'error', text: 'Failed to prepare asset for editing' });
        }
    };

    const dispatch = useDispatch();
    const handleEditSubmit = async () => {
        if (!assetDetails || !editFormType) return;

        try {
            setEditLoading(true);

            const formConfig = FORM_CONFIGS[editFormType] || [];
            const validation = FormValidator.validateForm(editFormData, formConfig);
            if (!validation.isValid) {
                setMessage({
                    type: 'error',
                    text: `Validation failed: ${validation.errors.join(', ')}`
                });
                return;
            }

            const apiRequest = AssetFormMapper.mapToApiRequest(editFormData, editFormType);

            if (editFormType === 'distribution_board') {
                if (!apiRequest.assetDistributionBoard) {
                    apiRequest.assetDistributionBoard = { placement: editFormData.placement };
                }
                apiRequest.assetDistributionBoard.distributionBoardCircuits = editingCircuits.map(circuit => ({
                    ...circuit,
                    id: circuit.id > 1000000000000 ? undefined : circuit.id
                }));
            }

            const response = await axios.put(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ASSETS}/${assetDetails.id}`,
                apiRequest,
                {
                    timeout: API_CONFIG.TIMEOUT,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const updatedAsset = response.data;
            
            dispatch(updateAssetInList(updatedAsset))

            setAssetDetails(updatedAsset);
            setIsEditMode(false);
            setEditFormData({});
            setMessage({
                type: 'success',
                text: `Asset "${updatedAsset.name}" updated successfully!`
            });

        } catch (error: any) {
            console.error('Error updating asset:', error);
            let errorMessage = 'Failed to update asset';
            if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            } else if (error.response?.data?.errorString) {
                errorMessage += `: ${error.response.data.errorString}`;
            } else if (error.message) {
                errorMessage += `: ${error.message}`;
            }
            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setEditLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditFormData({});
        setEditFormType('');
        setEditingField(null);
        setEditValue('');
    };

    const handleAddCircuit = () => {
        const newCircuit = {
            id: Date.now(),
            circuitId: `Circuit ${editingCircuits.length + 1}`,
            description: '',
            maxCurrent: 0,
            continousCurrent: 0,
            wireDimentions: '',
            meteredBy: '',
            driverTypes: 1,
            circuitNumber: editingCircuits.length + 1
        };
        setEditingCircuits([...editingCircuits, newCircuit]);
    };

    const handleRemoveCircuit = (index: number) => {
        if (window.confirm('Are you sure you want to remove this circuit?')) {
            const updatedCircuits = editingCircuits.filter((_, i) => i !== index);
            setEditingCircuits(updatedCircuits);
        }
    };

    // Universal editable detail renderer
    const renderEditableDetail = (
        label: string,
        value: any,
        fieldKey: string,
        suffix = '',
        formatter?: (val: any) => any,
        inputType: 'text' | 'number' | 'textarea' | 'select' = 'text',
        options?: Array<{ value: string, label: string }>
    ) => {
        if (!isEditMode && (
            value === undefined ||
            value === null ||
            value === '' ||
            value === '2025-06-27T08:33:08.266'
        )) return null;

        const display = formatter ? formatter(value) : value;
        const editableValue = editFormData[fieldKey] !== undefined ? editFormData[fieldKey] : value;

        if (!isEditMode && (display === null || display === undefined)) return null;

        return (
            <div className="detail-item" key={fieldKey}>
                <label>{label}:</label>
                {isEditMode ? (
                    <span
                        className="editable-field"
                        onClick={() => startFieldEdit(fieldKey, editableValue)}
                        title="Click to edit"
                    >
                        {editingField === fieldKey ? (
                            inputType === 'select' ? (
                                <select
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleFieldKeyDown}
                                    onBlur={saveFieldEdit}
                                    className="field-edit-select"
                                    autoFocus
                                >
                                    {options?.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            ) : inputType === 'textarea' ? (
                                <textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleFieldKeyDown}
                                    onBlur={saveFieldEdit}
                                    className="field-edit-textarea"
                                    autoFocus
                                    rows={3}
                                />
                            ) : (
                                <input
                                    type={inputType}
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleFieldKeyDown}
                                    onBlur={saveFieldEdit}
                                    className="field-edit-input"
                                    autoFocus
                                    min={inputType === 'number' ? 0 : undefined}
                                    step={inputType === 'number' ? 'any' : undefined}
                                />
                            )
                        ) : (
                            <>
                                {editableValue || display || '-'}{suffix}
                            </>
                        )}
                    </span>
                ) : (
                    <span>{display}{suffix}</span>
                )}
            </div>
        );
    };

    const handleBackToAsset = () => {
        const searchParams = new URLSearchParams(location.search);
        const parentsParam = searchParams.get('parents');

        const backUrl = parentsParam
            ? `/assets/${id}?parents=${parentsParam}`
            : `/assets/${id}`;

        navigate(backUrl);
    };

    const handleDeleteAsset = async (assetId: number) => {
        if (userRole !== 1) {
            setMessage({
                type: 'error',
                text: 'You dont have permission to delete asset'
            });
            return;
        }

        if (!window.confirm('Are you sure you want to delete this asset?')) return;

        try {
            await AssetService.deleteAsset(assetId);
            setMessage({ type: 'success', text: 'Asset deleted successfully!' });
            navigate('/');
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response) {
                setMessage({
                    type: 'error',
                    text: `Failed to delete asset: ${error.response.data.message ||
                        error.response.data.errorString ||
                        error.message
                        }`
                });
            } else {
                setMessage({ type: 'error', text: `Failed to delete asset: ${error}` });
            }
        }
    };

    if (loading) return (
        <div className="loading-overlay">
            <p className='loading-text'>Loading Details</p>
            <div className='loading-spinner'></div>
        </div>)
    if (!assetDetails || !selectedAsset) return <div>Asset not found</div>;

    const displayInfo = AssetUtils.formatAssetForDisplay(assetDetails);

    return (
        <div className="view-assets-container">
            {message && (
                <div className={message.type === 'success' ? 'success' : 'error'}>
                    {message.text}
                </div>
            )}

            {/* Asset Details */}
            <div className="asset-detail-container">
                <div className="asset-detail-main">
                    <div className="asset-detail-header">
                        <div className="asset-detail-header-left">
                            <div style={{ display: 'flex' }}>
                                <button onClick={handleBackToAsset} className="back-to-list-button">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                        width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="#000" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="19" y1="12" x2="5" y2="12"></line>
                                        <polyline points="12 19 5 12 12 5"></polyline>
                                    </svg>
                                </button>
                                <div className="asset-text">
                                    <h3 className="asset-title">{displayInfo.title}</h3>
                                    <div className="asset-type-badge">{displayInfo.type}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="asset-details-grid">
                        {/* Asset Name */}
                        {renderEditableDetail(t('name', 'Name'), assetDetails.name, 'name')}

                        {/* Description */}
                        {renderEditableDetail(t('description', 'Description'), assetDetails.description, 'description', '', undefined, 'textarea')}

                        {/* BUILDING FIELDS */}
                        {assetDetails.building && (
                            <>
                                {renderEditableDetail(t('admin:organization.location', 'Street Address'), assetDetails.location?.streetAddress1, 'streetAddress')}
                                {renderEditableDetail(t('admin:organization.location', 'Street Address') + ' 2', assetDetails.location?.streetAddress2, 'streetAddress2')}
                                {renderEditableDetail(t('admin:assets.location.zipCode', 'Zip Code'), assetDetails.location?.zipCode, 'zipCode')}
                                {renderEditableDetail(t('admin:assets.location.place', 'Place'), assetDetails.location?.place, 'place')}
                                {renderEditableDetail(t('admin:assets.location.country', 'Country'), assetDetails.location?.country, 'country')}
                                {renderEditableDetail(t('admin:organization.report.totalArea', 'Total Area'), assetDetails.building?.totalArea, 'totalArea', ' m²', (val) => val > 0 ? val : null, 'number')}
                                {renderEditableDetail(t('admin:assets.building.buildYear', 'Build Year'), assetDetails.building?.buildYear, 'buildYear', '', (val) => val !== '2025-06-27T08:33:08.266' ? new Date(val).getFullYear() : null, 'number')}
                                {renderEditableDetail(t('energy:renovatedYear', 'Renovated Year'), assetDetails.building?.renovatedYear, 'renovatedYear', '', (val) => (val !== '2025-06-27T08:33:08.266' && val !== assetDetails.building?.buildYear) ? new Date(val).getFullYear() : null, 'number')}
                                {renderEditableDetail(t('heating', 'Heating'), assetDetails.building?.heating, 'heating')}
                                {renderEditableDetail(t('ventilasjon', 'Ventilation'), assetDetails.building?.ventilation, 'ventilation')}
                                {renderEditableDetail(t('cooling', 'Cooling'), assetDetails.building?.cooling, 'cooling')}
                                {renderEditableDetail('Standards', assetDetails.building?.standards, 'standards')}
                                {renderEditableDetail('Building Automation System', assetDetails.building?.bas, 'bas')}
                                {renderEditableDetail(t('digitalSignage:solar.solarPanelCapacity', 'Solar Panel Power'), assetDetails.building?.installedSolarPanelCapacity, 'installedSolarPanelCapacity', ' kW', (val) => val > 0 ? val : null, 'number')}
                                {renderEditableDetail(t('iaq:propertyRegisterDesignation', 'Property Register Designation'), assetDetails.building?.propertyRegisterDesignation, 'propertyRegisterDesignation')}
                            </>
                        )}

                        {/* SOLAR FIELDS */}
                        {selectedAsset?.assetSolar && (
                            <>
                                {renderEditableDetail(t('admin:assets.solar.installedCapacity', 'Installed Power'), assetDetails?.assetSolar?.installedCapacity, 'installedCapacity', ' kW', undefined, 'number')}
                            </>
                        )}

                        {/* SOLAR STRING FIELDS */}
                        {assetDetails?.assetSolarString && (
                            <>
                                {renderEditableDetail(t('admin:assets.solar.numberOfPanels', 'Number of Panels'), assetDetails?.assetSolarString?.numberOfPanels, 'numberOfPanels', '', undefined, 'number')}
                            </>
                        )}

                        {/* STREET LIGHT FIELDS */}
                        {assetDetails?.assetStreetLight && (
                            <>
                                {renderEditableDetail(t('assets.voltage', 'Voltage'), assetDetails?.assetStreetLight?.voltage, 'voltage', ' V', undefined, 'number')}
                                {renderEditableDetail(t('admin:assets.ventilationManufacturer', 'Manufacturer'), assetDetails?.assetStreetLight?.manufacturerType, 'manufacturerType')}
                                {renderEditableDetail(t('admin:assets.lightSource', 'Light Source'), assetDetails?.assetStreetLight?.lightSource, 'lightSource')}
                                {renderEditableDetail(t('admin:assets.power', 'Power'), assetDetails?.assetStreetLight?.power, 'power', ' W', undefined, 'number')}
                                {renderEditableDetail(t('admin:assets.dimmable', 'Dimmable'), assetDetails?.assetStreetLight?.dimmable, 'dimmable', '', (val) => val === 'true' || val === true ? 'Yes' : 'No', 'select', [
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ])}
                            </>
                        )}

                        {/* JUNCTION BOX FIELDS */}
                        {assetDetails?.assetStreetLightJunctionBox && (
                            <>
                                {renderEditableDetail(t('admin:assets.totalTerminals', 'Total Terminals'), assetDetails?.assetStreetLightJunctionBox?.totalTerminals, 'totalTerminals', '', undefined, 'number')}
                                {renderEditableDetail(t('ignitionType', 'Ignition Type'), assetDetails?.assetStreetLightJunctionBox?.ignition, 'ignition')}
                            </>
                        )}

                        {/* VENTILATION FIELDS */}
                        {assetDetails?.assetVentilation && (
                            <>
                                {renderEditableDetail(t('admin:assets.ventilationPlacement', 'Placement'), assetDetails?.assetVentilation?.placement, 'placement')}
                                {renderEditableDetail(t('admin:assets.ventilationManufacturer', 'Manufacturer'), assetDetails?.assetVentilation?.manufacturer, 'manufacturer')}
                                {renderEditableDetail('Model', assetDetails?.assetVentilation?.model, 'model')}
                                {renderEditableDetail(t('admin:assets.ventilationRatedCapacity', 'Rated Capacity'), assetDetails?.assetVentilation?.ratedCapacity, 'ratedCapacity', ' m³/h', undefined, 'number')}
                                {renderEditableDetail(t('admin:assets.ventilationZone', 'Ventilation Zone'), assetDetails?.assetVentilation?.ventilationZone, 'ventilationZone')}
                                {renderEditableDetail(t('heating', 'Heating') + ' System', assetDetails?.assetVentilation?.heating, 'heatingSystem')}
                                {renderEditableDetail(t('cooling', 'Cooling') + ' System', assetDetails?.assetVentilation?.cooling, 'coolingSystem')}

                                {/* Heat Exchanger Type with complex mapping logic */}
                                {assetDetails?.assetVentilation?.heatExchangerType !== null &&
                                    assetDetails?.assetVentilation?.heatExchangerType !== undefined && (
                                        renderEditableDetail(
                                            t('admin:assets.ventilationHeatExchangerType', 'Heat Exchanger Type'),
                                            assetDetails?.assetVentilation.heatExchangerType,
                                            'heatExchangerType',
                                            '',
                                            (val) => {
                                                if (val === '0' || val === 0) return 'None';
                                                if (val === '1' || val === 1) return 'Plate Heat Exchanger';
                                                if (val === '2' || val === 2) return 'Rotary Heat Exchanger';
                                                if (val === '3' || val === 3) return 'Cross-flow Heat Exchanger';
                                                return val;
                                            },
                                            'select',
                                            [
                                                { value: '0', label: 'None' },
                                                { value: '1', label: 'Plate Heat Exchanger' },
                                                { value: '2', label: 'Rotary Heat Exchanger' },
                                                { value: '3', label: 'Cross-flow Heat Exchanger' }
                                            ]
                                        )
                                    )}

                                {renderEditableDetail(t('admin:assets.ventilationIsDemandControlled', 'Is Demand Controlled'), assetDetails?.assetVentilation?.isDemandControlled, 'isDemandControlled', '', (val) => val === 'true' || val === true ? 'Yes' : 'No', 'select', [
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ])}
                                {renderEditableDetail(t('', 'Is Pressure Controlled'), assetDetails?.assetVentilation?.isPressureControlled, 'isPressureControlled', '', (val) => val === 'true' || val === true ? 'Yes' : 'No', 'select', [
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ])}

                                {/* Driver Types with complex mapping logic */}
                                {assetDetails?.assetVentilation?.driverTypes && (
                                    renderEditableDetail(
                                        t('admin:assets.ventilationDriverType', 'Driver Types'),
                                        assetDetails?.assetVentilation.driverTypes,
                                        'driverTypes',
                                        '',
                                        (val) => {
                                            if (val === '1') return 'Variable Speed Drive';
                                            if (val === '2') return 'Fixed Speed';
                                            if (val === '3') return 'Multi-Speed';
                                            return val;
                                        },
                                        'select',
                                        [
                                            { value: '1', label: 'Variable Speed Drive' },
                                            { value: '2', label: 'Fixed Speed' },
                                            { value: '3', label: 'Multi-Speed' }
                                        ]
                                    )
                                )}
                            </>
                        )}

                        {/* HEAT PUMP FIELDS*/}
                        {assetDetails?.assetHeatPump && (
                            <>
                                {renderEditableDetail(t('admin:assets.ventilationManufacturer', 'Manufacturer'), assetDetails?.assetHeatPump?.manufacturer, 'heatPumpManufacturer')}
                                {renderEditableDetail('Model', assetDetails?.assetHeatPump?.model, 'heatPumpModel')}
                            </>
                        )}

                        {/* BOILER FIELDS*/}
                        {assetDetails?.assetBoiler && (
                            <>
                                {renderEditableDetail(t('admin:assets.ventilationManufacturer', 'Manufacturer'), assetDetails?.assetBoiler?.manufacturer, 'boilerManufacturer')}
                                {renderEditableDetail('Model', assetDetails?.assetBoiler?.model, 'boilerModel')}
                            </>
                        )}

                        {/* DISTRIBUTION BOARD FIELDS */}
                        {assetDetails?.assetDistributionBoard && (
                            <>
                                {renderEditableDetail(t('admin:assets.ventilationPlacement', 'Placement'), assetDetails?.assetDistributionBoard.placement, 'placement')}
                            </>
                        )}

                        {/* DISTRIBUTION BOARD CIRCUITS */}
                        {(assetDetails?.assetDistributionBoard?.assetDistributionBoardCircuits ||
                            (isEditMode && editFormType === 'distribution_board')) && (() => {

                                const circuits = isEditMode
                                    ? editingCircuits
                                    : assetDetails.assetDistributionBoard.assetDistributionBoardCircuits;

                                const hasMaxCurrent = circuits.some(c => c.maxCurrent && c.maxCurrent > 0);
                                const hasContinuousCurrent = circuits.some(c => c.continousCurrent && c.continousCurrent > 0);
                                const hasWireDimensions = circuits.some(c => c.wireDimentions && c.wireDimentions !== 'N/A' && c.wireDimentions.trim() !== '');
                                const hasMeteredBy = circuits.some(c => c.meteredBy && c.meteredBy.trim() !== '');
                                const hasDriverTypes = circuits.some(c => c.driverTypes && c.driverTypes !== 0);

                                if (!isEditMode && (!circuits || circuits.length === 0)) return null;

                                return (
                                    <div className="distribution-board-circuits">
                                        <div className="circuits-header">
                                            <h4>Distribution Board Circuits ({circuits.length})</h4>
                                            {isEditMode && (
                                                <div className="circuits-actions">
                                                    <button
                                                        type="button"
                                                        className="button button-small add-circuit-btn"
                                                        onClick={handleAddCircuit}
                                                    >
                                                        Add Circuit
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {isEditMode && circuits.length === 0 ? (
                                            <div className="no-circuits-message">
                                                No circuits added yet. Click "Add Circuit" to get started.
                                            </div>
                                        ) : (
                                            <div className="circuits-table-wrapper">
                                                <table className="circuits-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Circuit ID</th>
                                                            <th>Description</th>
                                                            {(hasMaxCurrent || isEditMode) && <th>Max Current (A)</th>}
                                                            {(hasContinuousCurrent || isEditMode) && <th>Continuous Current (A)</th>}
                                                            {(hasWireDimensions || isEditMode) && <th>Wire Dimensions</th>}
                                                            {(hasMeteredBy || isEditMode) && <th>Metered By</th>}
                                                            {/* {(hasDriverTypes || isEditMode) && <th>Driver Type</th>} */}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {circuits.map((circuit, index) => (
                                                            <tr key={circuit.id || index}>
                                                                {/* Circuit ID with trash icon */}
                                                                <td
                                                                    className={isEditMode ? 'editable-cell circuit-id-cell' : ''}
                                                                    onClick={isEditMode ? () => startCellEdit(index, 'circuitId', circuit.circuitId || '') : undefined}
                                                                    title={isEditMode ? 'Click to edit' : ''}
                                                                >
                                                                    {editingCell?.rowIndex === index && editingCell?.field === 'circuitId' ? (
                                                                        <input
                                                                            type="text"
                                                                            value={editValue}
                                                                            onChange={(e) => setEditValue(e.target.value)}
                                                                            onKeyDown={handleCellKeyDown}
                                                                            onBlur={saveCellEdit}
                                                                            className="cell-edit-input"
                                                                            autoFocus
                                                                            placeholder="e.g., C001"
                                                                        />
                                                                    ) : (
                                                                        <div className="circuit-id-content">
                                                                            <span>{circuit.circuitId || '-'}</span>
                                                                            {isEditMode && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="delete-circuit-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveCircuit(index);
                                                                                    }}
                                                                                    title="Remove circuit"
                                                                                >
                                                                                    🗑️
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                {/* Description */}
                                                                <td
                                                                    className={isEditMode ? 'editable-cell' : ''}
                                                                    onClick={isEditMode ? () => startCellEdit(index, 'description', circuit.description || '') : undefined}
                                                                    title={isEditMode ? 'Click to edit' : ''}
                                                                >
                                                                    {editingCell?.rowIndex === index && editingCell?.field === 'description' ? (
                                                                        <input
                                                                            type="text"
                                                                            value={editValue}
                                                                            onChange={(e) => setEditValue(e.target.value)}
                                                                            onKeyDown={handleCellKeyDown}
                                                                            onBlur={saveCellEdit}
                                                                            className="cell-edit-input"
                                                                            autoFocus
                                                                            placeholder="Circuit description"
                                                                        />
                                                                    ) : (
                                                                        <span>{circuit.description || '-'}</span>
                                                                    )}
                                                                </td>

                                                                {/* Max Current */}
                                                                {(hasMaxCurrent || isEditMode) && (
                                                                    <td
                                                                        className={isEditMode ? 'editable-cell' : ''}
                                                                        onClick={isEditMode ? () => startCellEdit(index, 'maxCurrent', circuit.maxCurrent?.toString() || '') : undefined}
                                                                        title={isEditMode ? 'Click to edit' : ''}
                                                                    >
                                                                        {editingCell?.rowIndex === index && editingCell?.field === 'maxCurrent' ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editValue}
                                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                                onKeyDown={handleCellKeyDown}
                                                                                onBlur={saveCellEdit}
                                                                                className="cell-edit-input"
                                                                                autoFocus
                                                                                placeholder="0"
                                                                                min="0"
                                                                                step="0.1"
                                                                            />
                                                                        ) : (
                                                                            <span>{circuit.maxCurrent > 0 ? circuit.maxCurrent : '-'}</span>
                                                                        )}
                                                                    </td>
                                                                )}

                                                                {/* Continuous Current */}
                                                                {(hasContinuousCurrent || isEditMode) && (
                                                                    <td
                                                                        className={isEditMode ? 'editable-cell' : ''}
                                                                        onClick={isEditMode ? () => startCellEdit(index, 'continousCurrent', circuit.continousCurrent?.toString() || '') : undefined}
                                                                        title={isEditMode ? 'Click to edit' : ''}
                                                                    >
                                                                        {editingCell?.rowIndex === index && editingCell?.field === 'continousCurrent' ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editValue}
                                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                                onKeyDown={handleCellKeyDown}
                                                                                onBlur={saveCellEdit}
                                                                                className="cell-edit-input"
                                                                                autoFocus
                                                                                placeholder="0"
                                                                                min="0"
                                                                                step="0.1"
                                                                            />
                                                                        ) : (
                                                                            <span>{circuit.continousCurrent > 0 ? circuit.continousCurrent : '-'}</span>
                                                                        )}
                                                                    </td>
                                                                )}

                                                                {/* Wire Dimensions */}
                                                                {(hasWireDimensions || isEditMode) && (
                                                                    <td
                                                                        className={isEditMode ? 'editable-cell' : ''}
                                                                        onClick={isEditMode ? () => startCellEdit(index, 'wireDimentions', circuit.wireDimentions || '') : undefined}
                                                                        title={isEditMode ? 'Click to edit' : ''}
                                                                    >
                                                                        {editingCell?.rowIndex === index && editingCell?.field === 'wireDimentions' ? (
                                                                            <input
                                                                                type="text"
                                                                                value={editValue}
                                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                                onKeyDown={handleCellKeyDown}
                                                                                onBlur={saveCellEdit}
                                                                                className="cell-edit-input"
                                                                                autoFocus
                                                                                placeholder="e.g., 2.5mm²"
                                                                            />
                                                                        ) : (
                                                                            <span>{circuit.wireDimentions && circuit.wireDimentions !== 'N/A' ? circuit.wireDimentions : '-'}</span>
                                                                        )}
                                                                    </td>
                                                                )}

                                                                {/* Metered By */}
                                                                {(hasMeteredBy || isEditMode) && (
                                                                    <td
                                                                        className={isEditMode ? 'editable-cell' : ''}
                                                                        onClick={isEditMode ? () => startCellEdit(index, 'meteredBy', circuit.meteredBy || '') : undefined}
                                                                        title={isEditMode ? 'Click to edit' : ''}
                                                                    >
                                                                        {editingCell?.rowIndex === index && editingCell?.field === 'meteredBy' ? (
                                                                            <input
                                                                                type="text"
                                                                                value={editValue}
                                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                                onKeyDown={handleCellKeyDown}
                                                                                onBlur={saveCellEdit}
                                                                                className="cell-edit-input"
                                                                                autoFocus
                                                                                placeholder="Meter identifier"
                                                                            />
                                                                        ) : (
                                                                            <span>{circuit.meteredBy || '-'}</span>
                                                                        )}
                                                                    </td>
                                                                )}

                                                                {/* Driver Type
                                                                {(hasDriverTypes || isEditMode) && (
                                                                    <td
                                                                        className={isEditMode ? 'editable-cell' : ''}
                                                                        onClick={isEditMode ? () => startDriverTypeEdit(index, circuit.driverTypes || 1) : undefined}
                                                                        title={isEditMode ? 'Click to edit' : ''}
                                                                    >
                                                                        {editingCell?.rowIndex === index && editingCell?.field === 'driverTypes' ? (
                                                                            <select
                                                                                value={editValue}
                                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                                onKeyDown={handleCellKeyDown}
                                                                                onBlur={saveCellEdit}
                                                                                className="cell-edit-select"
                                                                                autoFocus
                                                                            >
                                                                                <option value="1">Standard</option>
                                                                                <option value="2">Variable</option>
                                                                                <option value="3">Multi-Speed</option>
                                                                            </select>
                                                                        ) : (
                                                                            <span>
                                                                                {circuit.driverTypes === 1 ? 'Standard' :
                                                                                    circuit.driverTypes === 2 ? 'Variable' :
                                                                                        circuit.driverTypes === 3 ? 'Multi-Speed' :
                                                                                            circuit.driverTypes || 'Standard'}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                )} */}

                                                                {/* Actions (only in edit mode) - REMOVED */}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                        {/* ACTIVE STATUS */}
                        <div className="detail-item">
                            <label>Status:</label>
                            <span className={`status ${assetDetails.isActive ? t('admin:assets.active', 'active') : t('admin:inactive', 'inactive')}`}>
                                {assetDetails.isActive ? t('admin:assets.active', 'Active') : t('admin:inactive', 'Inactive')}
                            </span>
                        </div>
                    </div>

                </div>

                {/* Action Buttons */}
                <div className="asset-actions">
                    {!isEditMode ? (
                        <>
                            <button
                                className="button button-primary"
                                onClick={handleEditAsset}
                                disabled={loading}
                            >
                                {t('edit', 'Edit')} {t('admin:slideShows.asset', 'Asset')}
                            </button>
                            <button
                                className="button button-danger"
                                onClick={() => handleDeleteAsset(assetDetails.id)}
                                disabled={loading}
                            >
                                {loading ? t('deleting') : t('delete') + ' ' + t('admin:slideShows.asset')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="button button-success"
                                onClick={handleEditSubmit}
                                disabled={editLoading}
                            >
                                {editLoading ? t('saving', 'Saving...') : t('save', 'Save Changes')}
                            </button>
                            <button
                                className="button button-secondary"
                                onClick={handleCancelEdit}
                                disabled={editLoading}
                            >
                                {t('cancel', 'Cancel')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssetDetail;